import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { API_URL } from './AdminShared';
import { TrendingUp } from 'lucide-react';

export const AnalyticsTab = () => {
  const [data, setData] = useState(null);
  const [days, setDays] = useState(30);
  const [loading, setLoading] = useState(true);

  const fetchAnalytics = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/api/admin/analytics/summary?days=${days}`);
      setData(res.data);
    } catch (err) {
      toast.error('Failed to load analytics');
    } finally {
      setLoading(false);
    }
  }, [days]);

  useEffect(() => { fetchAnalytics(); }, [fetchAnalytics]);

  if (loading) return <div className="flex justify-center py-20"><div className="w-8 h-8 border-2 border-[#E07A3A] border-t-transparent rounded-full animate-spin" /></div>;
  if (!data) return <p className="text-slate-500 text-center py-12">No analytics data available yet.</p>;

  const eventLabels = {
    page_view: 'Page Views',
    login_attempt: 'Login Attempts',
    registration_attempt: 'Registrations',
    forgot_password: 'Password Resets',
    property_create: 'Properties Created',
    payment_initiated: 'Payments Initiated',
  };

  const countryFlags = { UK: '\u{1F1EC}\u{1F1E7}', IN: '\u{1F1EE}\u{1F1F3}', CA: '\u{1F1E8}\u{1F1E6}' };

  const maxDailyEvents = Math.max(...(data.daily_trend?.map(d => d.events) || [1]), 1);

  return (
    <div className="space-y-8" data-testid="analytics-tab">
      {/* Period Selector */}
      <div className="flex items-center justify-between">
        <h2 className="font-['Playfair_Display'] text-2xl text-[#1A1A1A]">Visitor Analytics</h2>
        <div className="flex gap-2">
          {[7, 14, 30, 90].map(d => (
            <button key={d} onClick={() => setDays(d)}
              className={`px-4 py-2 text-xs tracking-wider font-medium transition-colors ${days === d ? 'bg-[#E07A3A] text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`}
              data-testid={`analytics-period-${d}`}
            >{d}D</button>
          ))}
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="luxury-card p-5" data-testid="analytics-visitors">
          <p className="text-xs text-slate-500 tracking-wider uppercase mb-1">Unique Visitors</p>
          <p className="text-3xl font-semibold text-[#1A1A1A]">{data.unique_visitors.toLocaleString('en-GB')}</p>
          <p className="text-xs text-slate-400 mt-1">Last {days} days</p>
        </div>
        <div className="luxury-card p-5" data-testid="analytics-events">
          <p className="text-xs text-slate-500 tracking-wider uppercase mb-1">Total Events</p>
          <p className="text-3xl font-semibold text-[#1A1A1A]">{data.total_events.toLocaleString('en-GB')}</p>
          <p className="text-xs text-slate-400 mt-1">All interactions</p>
        </div>
        <div className="luxury-card p-5" data-testid="analytics-registrations">
          <p className="text-xs text-slate-500 tracking-wider uppercase mb-1">New Sign-ups</p>
          <p className="text-3xl font-semibold text-[#E07A3A]">{data.registration_attempts.toLocaleString('en-GB')}</p>
          <p className="text-xs text-slate-400 mt-1">Last {days} days</p>
          {data.registration_breakdown && (
            <div className="mt-3 pt-3 border-t border-slate-700 space-y-1">
              <div className="flex justify-between text-[11px] text-slate-400">
                <span>Form clicks</span>
                <span className="font-semibold text-slate-200" data-testid="analytics-reg-form-clicks">
                  {(data.registration_breakdown.form_submit_clicks || 0).toLocaleString('en-GB')}
                </span>
              </div>
              <div className="flex justify-between text-[11px] text-slate-400">
                <span>Completed via form</span>
                <span className="font-semibold text-slate-200" data-testid="analytics-reg-completed">
                  {(data.registration_breakdown.completed_via_form || 0).toLocaleString('en-GB')}
                </span>
              </div>
              <div className="flex justify-between text-[11px] text-slate-400">
                <span>Failed attempts</span>
                <span className="font-semibold text-rose-400" data-testid="analytics-reg-failed">
                  {(data.registration_breakdown.failed_attempts || 0).toLocaleString('en-GB')}
                </span>
              </div>
              <div className="flex justify-between text-[11px] text-slate-400">
                <span>Actual users created</span>
                <span className="font-semibold text-emerald-400" data-testid="analytics-reg-actual">
                  {(data.registration_breakdown.actual_users_created || 0).toLocaleString('en-GB')}
                </span>
              </div>
              {data.registration_breakdown.failure_reasons?.length > 0 && (
                <div className="mt-3 pt-3 border-t border-slate-700/60">
                  <p className="text-[10px] text-slate-500 tracking-wider uppercase mb-1.5 font-bold">Top Failure Reasons</p>
                  <div className="space-y-1" data-testid="analytics-reg-failure-reasons">
                    {data.registration_breakdown.failure_reasons.slice(0, 5).map((r) => (
                      <div key={r.reason} className="flex justify-between text-[11px]">
                        <span className="text-rose-300/80 font-mono">{r.reason}</span>
                        <span className="font-semibold text-rose-400">{r.count}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
        <div className="luxury-card p-5">
          <p className="text-xs text-slate-500 tracking-wider uppercase mb-1">Avg Daily Visitors</p>
          <p className="text-3xl font-semibold text-[#1A1A1A]">{days > 0 ? Math.round(data.unique_visitors / days) : 0}</p>
          <p className="text-xs text-slate-400 mt-1">Per day average</p>
        </div>
      </div>

      {/* Daily Trend Chart */}
      {data.daily_trend?.length > 0 && (
        <div className="luxury-card p-6">
          <h3 className="font-semibold text-[#1A1A1A] mb-4 text-sm tracking-wider uppercase">Daily Traffic</h3>
          <div className="flex items-end gap-1 h-40" data-testid="analytics-daily-chart">
            {data.daily_trend.map((day, i) => (
              <div key={day.date} className="flex-1 flex flex-col items-center gap-1 group relative">
                <div className="absolute -top-8 left-1/2 -translate-x-1/2 bg-[#0F172A] text-white text-[10px] px-2 py-1 opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap pointer-events-none z-10">
                  {day.date}: {day.events} events, {day.visitors} visitors
                </div>
                <div
                  className="w-full bg-[#E07A3A]/80 hover:bg-[#E07A3A] transition-colors rounded-t"
                  style={{ height: `${Math.max((day.events / maxDailyEvents) * 100, 4)}%` }}
                />
                {(i === 0 || i === data.daily_trend.length - 1 || i % Math.max(Math.floor(data.daily_trend.length / 6), 1) === 0) && (
                  <span className="text-[9px] text-slate-400 -rotate-45 origin-top-left mt-1">{day.date.slice(5)}</span>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="grid md:grid-cols-2 gap-6">
        {/* Country Breakdown */}
        <div className="luxury-card p-6">
          <h3 className="font-semibold text-[#1A1A1A] mb-4 text-sm tracking-wider uppercase">Visitors by Country</h3>
          {data.by_country?.length > 0 ? (
            <div className="space-y-3" data-testid="analytics-by-country">
              {data.by_country.map(c => {
                const maxEvents = data.by_country[0]?.events || 1;
                return (
                  <div key={c.country} className="flex items-center gap-3">
                    <span className="text-lg w-8">{countryFlags[c.country] || c.country}</span>
                    <div className="flex-1">
                      <div className="flex justify-between mb-1">
                        <span className="text-sm font-medium text-[#1A1A1A]">{c.country}</span>
                        <span className="text-xs text-slate-500">{c.visitors} visitors · {c.events} events</span>
                      </div>
                      <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                        <div className="h-full bg-[#E07A3A] rounded-full transition-all" style={{ width: `${(c.events / maxEvents) * 100}%` }} />
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : <p className="text-slate-400 text-sm">No country data yet</p>}
        </div>

        {/* Top Pages */}
        <div className="luxury-card p-6">
          <h3 className="font-semibold text-[#1A1A1A] mb-4 text-sm tracking-wider uppercase">Top Pages</h3>
          {data.top_pages?.length > 0 ? (
            <div className="space-y-2" data-testid="analytics-top-pages">
              {data.top_pages.map((p, i) => (
                <div key={p.page} className="flex items-center justify-between py-2 border-b border-slate-50 last:border-0">
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-slate-400 w-5">{i + 1}.</span>
                    <span className="text-sm text-[#1A1A1A] font-medium">{p.page || '/'}</span>
                  </div>
                  <span className="text-xs text-slate-500 font-medium">{p.views} views</span>
                </div>
              ))}
            </div>
          ) : <p className="text-slate-400 text-sm">No page views yet</p>}
        </div>
      </div>

      {/* Form Interactions */}
      <div className="luxury-card p-6">
        <h3 className="font-semibold text-[#1A1A1A] mb-4 text-sm tracking-wider uppercase">Form & Action Events</h3>
        {data.form_interactions?.length > 0 ? (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4" data-testid="analytics-form-events">
            {data.form_interactions.map(f => (
              <div key={f.event} className="text-center p-4 bg-slate-50">
                <p className="text-2xl font-semibold text-[#1A1A1A]">{f.count}</p>
                <p className="text-xs text-slate-500 mt-1">{eventLabels[f.event] || f.event.replace(/_/g, ' ')}</p>
              </div>
            ))}
          </div>
        ) : <p className="text-slate-400 text-sm">No form events yet. Data will appear as users interact with the site.</p>}
      </div>

      {/* Google Analytics Note */}
      <div className="bg-blue-50 border border-blue-200 p-4 flex items-start gap-3">
        <TrendingUp className="h-5 w-5 text-blue-600 mt-0.5 flex-shrink-0" />
        <div>
          <p className="text-sm text-blue-800 font-medium">Google Analytics Connected</p>
          <p className="text-xs text-blue-600 mt-1">
            For deeper traffic insights (sources, devices, demographics), visit{' '}
            <a href="https://analytics.google.com" target="_blank" rel="noopener noreferrer" className="underline font-medium">analytics.google.com</a>
          </p>
        </div>
      </div>
    </div>
  );
};
